import { m } from 'framer-motion';
import { useState, useCallback, useEffect, useRef } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useRouter } from 'src/routes/hooks';
// utils
import axiosInstance from 'src/utils/axios';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';
import NotificationDetailDialog from './notification-detail-dialog';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const router = useRouter();
  const smUp = useResponsive('up', 'sm');

  const [currentTab, setCurrentTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [activeTasks, setActiveTasks] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const detailDialog = useBoolean();

  const pollTimerRef = useRef(null);

  const fetchNotifications = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const res = await axiosInstance.get('/api/notifications/');
      const data = res.data || {};
      setNotifications(data.results || []);
      setActiveTasks(data.active_tasks || []);
      setUnreadCount(data.unread_count || 0);
      setTotalCount(data.total_count || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, []);

  // Poll notifications and active background tasks
  useEffect(() => {
    fetchNotifications();

    const intervalMs = activeTasks.length > 0 ? 3000 : 25000;
    pollTimerRef.current = setInterval(() => {
      fetchNotifications(true);
    }, intervalMs);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [fetchNotifications, activeTasks.length]);

  // When drawer opens, refresh immediately
  useEffect(() => {
    if (drawer.value) {
      fetchNotifications(true);
    }
  }, [drawer.value, fetchNotifications]);

  const handleChangeTab = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await axiosInstance.post('/api/notifications/mark-all-read/');
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          isUnRead: false,
        }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleReadNotification = async (notification) => {
    if (!notification.is_read) {
      try {
        await axiosInstance.post(`/api/notifications/${notification.id}/mark-read/`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true, isUnRead: false } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification read:', err);
      }
    }

    setSelectedNotification(notification);
    detailDialog.onTrue();
  };

  const handleNavigate = (url) => {
    detailDialog.onFalse();
    drawer.onFalse();
    if (url) {
      router.push(url);
    }
  };

  const filteredNotifications = notifications.filter((item) => {
    if (currentTab === 'unread') return !item.is_read;
    if (currentTab === 'archived') return item.is_read;
    return true;
  });

  const tabsConfig = [
    {
      value: 'all',
      label: 'All',
      count: totalCount,
    },
    {
      value: 'unread',
      label: 'Unread',
      count: unreadCount,
    },
    {
      value: 'archived',
      label: 'Read',
      count: Math.max(0, totalCount - unreadCount),
    },
  ];

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!unreadCount && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Refresh">
        <IconButton onClick={() => fetchNotifications(false)}>
          <Iconify icon="solar:refresh-circle-bold" />
        </IconButton>
      </Tooltip>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderActiveTasks = activeTasks.length > 0 && (
    <Box sx={{ p: 2, bgcolor: 'background.neutral', borderBottom: (theme) => `dashed 1px ${theme.palette.divider}` }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <CircularProgress size={16} thickness={5} color="primary" />
        <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
          Active Background Tasks ({activeTasks.length})
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {activeTasks.map((task) => (
          <Card key={task.task_id} sx={{ p: 1.5, border: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" noWrap sx={{ maxWidth: 220 }}>
                {task.title}
              </Typography>
              <Label variant="soft" color="info" sx={{ fontSize: 11 }}>
                {task.progress_percent}%
              </Label>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={task.progress_percent || 0}
              sx={{ height: 6, borderRadius: 1, my: 0.8 }}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {task.status_message || `${task.status}...`}
              </Typography>
              {task.progress_total > 0 && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                  {task.progress_current}/{task.progress_total}
                </Typography>
              )}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ px: 2 }}>
      {tabsConfig.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'default') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 2,
            },
          }}
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      {loading && notifications.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress size={32} />
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1 }}>
            Loading notifications...
          </Typography>
        </Stack>
      ) : filteredNotifications.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8, px: 3, textAlign: 'center' }}>
          <Iconify icon="solar:bell-off-bold-duotone" width={48} sx={{ color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            No notifications found
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {currentTab === 'unread' ? "You're all caught up!" : 'You have no notifications in this tab.'}
          </Typography>
        </Stack>
      ) : (
        <List disablePadding>
          {filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={handleReadNotification}
            />
          ))}
        </List>
      )}
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        {renderActiveTasks}

        <Box sx={{ py: 1 }}>
          {renderTabs}
        </Box>

        <Divider />

        {renderList}
      </Drawer>

      <NotificationDetailDialog
        open={detailDialog.value}
        onClose={detailDialog.onFalse}
        notification={selectedNotification}
        onNavigate={handleNavigate}
      />
    </>
  );
}
